import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ArrowLeft } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatR } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { BackButton } from "@/components/BackButton";

export default function CartPage() {
  const { items, setQty, remove, subtotal, settings } = useCart();
  const navigate = useNavigate();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="cart-page">
      <div className="mb-4">
        <BackButton onClick={() => navigate(sessionStorage.getItem("btm_last_shop") || "/shop")} label="Continue shopping" testId="cart-back-button" />
      </div>
      <Reveal>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">Shopping cart</h1>
      </Reveal>

      {items.length === 0 ? (
        <div className="mt-10 bg-white rounded-3xl border border-slate-200/80 p-16 text-center" data-testid="cart-page-empty">
          <p className="font-display font-bold text-navy text-xl">Your cart is empty</p>
          <p className="text-sm text-slate-500 mt-2">Stock up for the school year — it takes seconds.</p>
          <Link
            to="/shop"
            className="inline-flex mt-6 px-7 py-3.5 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm items-center gap-2 transition-colors"
            data-testid="empty-cart-shop-link"
          >
            Browse products <ArrowRight size={15} />
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
          <div className="bg-white rounded-3xl border border-slate-200/80 divide-y divide-slate-100" data-testid="cart-items-list">
            {items.map((i) => (
              <div key={i.id} className="p-5 flex gap-4 items-center" data-testid={`cartpage-item-${i.id}`}>
                <img src={i.image} alt={i.name} className="w-20 h-20 rounded-2xl object-cover border border-slate-100" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-semibold text-navy text-sm sm:text-base">{i.name}</p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">{formatR(i.price)} each</p>
                  <div className="flex items-center gap-2 mt-2.5">
                    <button onClick={() => setQty(i.id, i.qty - 1)} disabled={i.qty <= 1} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center disabled:opacity-40 hover:border-gold transition-colors" data-testid={`cartpage-minus-${i.id}`} aria-label="Decrease">
                      <Minus size={13} />
                    </button>
                    <span className="w-7 text-center text-sm font-bold" data-testid={`cartpage-qty-${i.id}`}>{i.qty}</span>
                    <button onClick={() => setQty(i.id, i.qty + 1)} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:border-gold transition-colors" data-testid={`cartpage-plus-${i.id}`} aria-label="Increase">
                      <Plus size={13} />
                    </button>
                    <button onClick={() => remove(i.id)} className="ml-3 p-1.5 text-slate-400 hover:text-red-500 transition-colors" data-testid={`cartpage-remove-${i.id}`} aria-label="Remove item">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                <p className="font-mono font-bold text-navy">{formatR(i.price * i.qty)}</p>
              </div>
            ))}
          </div>

          <div className="bg-navy text-white rounded-3xl p-7 sticky top-36" data-testid="cart-summary">
            <h3 className="font-display font-bold text-lg">Order summary</h3>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between text-white/70">
                <span>Subtotal</span>
                <span className="font-mono text-white" data-testid="summary-subtotal">{formatR(subtotal)}</span>
              </div>
              <div className="flex justify-between text-white/70">
                <span>Delivery</span>
                <span className="font-mono text-white">
                  {subtotal >= (settings.free_delivery_over || 750) ? "FREE" : `from ${formatR(settings.delivery_fee || 85)}`}
                </span>
              </div>
              <div className="border-t border-white/15 pt-3 flex justify-between font-bold">
                <span>Estimated total</span>
                <span className="font-mono text-gold" data-testid="summary-total">{formatR(subtotal)}</span>
              </div>
            </div>
            <button
              onClick={() => navigate("/checkout")}
              className="mt-6 w-full h-12 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors gold-glow"
              data-testid="cartpage-checkout-button"
            >
              Proceed to checkout <ArrowRight size={15} />
            </button>
            <Link to="/shop" className="mt-4 flex items-center justify-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors" data-testid="continue-shopping-link">
              <ArrowLeft size={13} /> Continue shopping
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
